// frontend/src/utils/NotificationPriorityManager.js

/**
 * 🎯 GERENCIADOR DE PRIORIDADE DE NOTIFICAÇÕES
 * 
 * Sistema profissional de controle de notificações:
 * - Define quais notificações mostram toast push
 * - Evita duplicação de toasts (controle por localStorage)
 * - Gerencia TTL (Time To Live) de notificações
 */

const TOAST_TTL = 5 * 60 * 1000; // 5 minutos em ms
const SHOWN_TOASTS_KEY = 'pagmus_shown_toasts';

/**
 * 🔔 PRIORIDADES DE NOTIFICAÇÃO
 */
export const NotificationPriority = {
  HIGH: 'high',      // Mostra toast + som + dropdown
  LOW: 'low',        // Apenas dropdown (silencioso)
  SILENT: 'silent'   // Apenas no banco, sem UI imediata
};

/**
 * 📋 MAPA DE PRIORIDADES POR TIPO DE NOTIFICAÇÃO
 */
const NOTIFICATION_PRIORITY_MAP = {
  // 🔔 ALTA PRIORIDADE - Mostra toast push
  'ticket_replied': NotificationPriority.HIGH,
  'affiliate_signup': NotificationPriority.HIGH,
  'affiliate_approved': NotificationPriority.HIGH,
  'affiliate_rejected': NotificationPriority.HIGH,
  'affiliate_invite_pending': NotificationPriority.HIGH, // Convite de afiliado
  'withdrawal_approved': NotificationPriority.HIGH,
  'withdrawal_rejected': NotificationPriority.HIGH,
  'system_alert': NotificationPriority.HIGH,
  
  // 📊 BAIXA PRIORIDADE - Apenas dropdown (sem toast)
  'sale_processed': NotificationPriority.LOW,
  'commission_received': NotificationPriority.LOW,
  'balance_available': NotificationPriority.LOW,
  'commission_available': NotificationPriority.LOW,
  
  // 🔇 SILENCIOSO - Apenas no banco
  'system_info': NotificationPriority.SILENT
};

/**
 * 🎯 Obter prioridade de uma notificação
 */
export const getNotificationPriority = (notificationType) => {
  return NOTIFICATION_PRIORITY_MAP[notificationType] || NotificationPriority.LOW;
};

/**
 * 🔔 Verificar se deve mostrar toast para esta notificação
 */
export const shouldShowToast = (notification) => {
  const priority = getNotificationPriority(notification.type);
  
  // Não mostrar toast para prioridade baixa ou silenciosa
  if (priority !== NotificationPriority.HIGH) {
    return false;
  }
  
  // Verificar se já foi mostrado recentemente
  if (wasToastShownRecently(notification.id)) {
    return false;
  }
  
  return true;
};

/**
 * 💾 Verificar se toast já foi mostrado recentemente
 */
const wasToastShownRecently = (notificationId) => {
  try {
    const shownToasts = getShownToasts();
    const now = Date.now();
    
    // Verificar se existe e se ainda está dentro do TTL
    if (shownToasts[notificationId]) {
      const shownAt = shownToasts[notificationId];
      return (now - shownAt) < TOAST_TTL;
    }
    
    return false;
  } catch (error) {
    console.error('❌ Erro ao verificar toast:', error);
    return false;
  }
};

/**
 * 📝 Marcar toast como mostrado
 */
export const markToastAsShown = (notificationId) => {
  try {
    const shownToasts = getShownToasts();
    shownToasts[notificationId] = Date.now();
    
    // Limpar toasts expirados antes de salvar
    cleanupExpiredToasts(shownToasts);
    
    localStorage.setItem(SHOWN_TOASTS_KEY, JSON.stringify(shownToasts));
  } catch (error) {
    console.error('❌ Erro ao marcar toast:', error);
  }
};

/**
 * 📋 Obter lista de toasts já mostrados
 */
const getShownToasts = () => {
  try {
    const stored = localStorage.getItem(SHOWN_TOASTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('❌ Erro ao obter toasts:', error);
    return {};
  }
};

/**
 * 🧹 Limpar toasts expirados (mais antigos que TTL)
 */
const cleanupExpiredToasts = (shownToasts) => {
  const now = Date.now();
  
  Object.keys(shownToasts).forEach(id => {
    const shownAt = shownToasts[id];
    if ((now - shownAt) > TOAST_TTL) {
      delete shownToasts[id];
    }
  });
};

/**
 * 🔄 Limpar todos os toasts (útil para debug/logout)
 */
export const clearAllShownToasts = () => {
  try {
    localStorage.removeItem(SHOWN_TOASTS_KEY);
  } catch (error) {
    console.error('❌ Erro ao limpar toasts:', error);
  }
};

/**
 * 📊 Obter estatísticas de prioridade
 */
export const getNotificationStats = (notifications) => {
  const stats = {
    high: 0,
    low: 0,
    silent: 0,
    total: notifications.length
  };
  
  notifications.forEach(notification => {
    const priority = getNotificationPriority(notification.type);
    if (priority === NotificationPriority.HIGH) stats.high++;
    else if (priority === NotificationPriority.LOW) stats.low++;
    else stats.silent++;
  });
  
  return stats;
};

/**
 * 🎨 Obter ícone e cor baseado na prioridade
 */
export const getPriorityStyle = (notificationType) => {
  const priority = getNotificationPriority(notificationType);
  
  switch (priority) {
    case NotificationPriority.HIGH:
      return {
        icon: 'mdi:bell-ring',
        color: 'danger',
        badge: 'danger'
      };
    case NotificationPriority.LOW:
      return {
        icon: 'mdi:bell',
        color: 'info',
        badge: 'info'
      };
    case NotificationPriority.SILENT:
      return {
        icon: 'mdi:bell-off',
        color: 'secondary',
        badge: 'secondary'
      };
    default:
      return {
        icon: 'mdi:bell',
        color: 'primary',
        badge: 'primary'
      };
  }
};

export default {
  NotificationPriority,
  getNotificationPriority,
  shouldShowToast,
  markToastAsShown,
  clearAllShownToasts,
  getNotificationStats,
  getPriorityStyle
};

